import { Box, TextField, Tooltip } from '@mui/material';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import ExhibitionOriginChip from 'src/components/exhibition-origin-chip';
import Label from 'src/components/label';
import { BenefitStatusCell, BenefitStatusHeader } from '../../components/benefit-status-cell';
import {
  BenefitDateCell,
  BenefitImageCell,
  BenefitTypeCell,
} from '../../components/benefit-table-cells';

type BulkEditableBenefitRow = {
  benefitId: string;
  benefitType: 'coupon' | 'product';
  image: string;
  name: string;
  priority: number | null;
};

type BulkEditFieldChange = (
  row: BulkEditableBenefitRow,
  field: 'name' | 'priority',
  value: string | number | null
) => void;

type GetBenefitsColumnsParams = {
  formatDate: (isoDate: string) => string;
  formatPoints: (points: number) => string;
  handleChangeStatus: (benefitId: string, newStatus: 'active' | 'inactive') => void;
  tierColumnWidth: number;
  isBulkEditMode?: boolean;
  isBulkEditLocked?: boolean;
  onBulkEditFieldChange?: BulkEditFieldChange;
};

const BULK_EDIT_INPUT_COMMIT_DELAY_MS = 120;

const renderReadOnlyTextCell = (value: string | number) => (
  <Tooltip title={value} slotProps={{ tooltip: { sx: { borderRadius: 1 } } }}>
    <Box
      component="span"
      sx={{
        width: 1,
        minWidth: 0,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      }}
    >
      {value}
    </Box>
  </Tooltip>
);

const useBufferedInput = (externalValue: string, onCommit: (value: string) => void) => {
  const [value, setValue] = useState(externalValue);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const latestValueRef = useRef(externalValue);
  const lastEmittedValueRef = useRef(externalValue);
  const onCommitRef = useRef(onCommit);

  onCommitRef.current = onCommit;

  useEffect(() => {
    if (externalValue === lastEmittedValueRef.current) {
      return;
    }

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    latestValueRef.current = externalValue;
    lastEmittedValueRef.current = externalValue;
    setValue(externalValue);
  }, [externalValue]);

  useEffect(
    () => () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    },
    []
  );

  const commit = useCallback((nextValue: string) => {
    lastEmittedValueRef.current = nextValue;
    onCommitRef.current(nextValue);
  }, []);

  const setBufferedValue = useCallback(
    (nextValue: string) => {
      setValue(nextValue);
      latestValueRef.current = nextValue;

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      timeoutRef.current = setTimeout(() => {
        timeoutRef.current = null;
        commit(nextValue);
      }, BULK_EDIT_INPUT_COMMIT_DELAY_MS);
    },
    [commit]
  );

  const flush = useCallback(() => {
    const hasPendingCommit = Boolean(timeoutRef.current);

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    const nextValue = latestValueRef.current;
    if (
      hasPendingCommit ||
      (nextValue !== externalValue && nextValue !== lastEmittedValueRef.current)
    ) {
      flushSync(() => commit(nextValue));
    }
  }, [commit, externalValue]);

  return { value, setBufferedValue, flush };
};

type BulkEditCellProps = {
  row: BulkEditableBenefitRow;
  disabled: boolean;
  onChange?: BulkEditFieldChange;
};

const BenefitNameEditCell = memo(({ row, disabled, onChange }: BulkEditCellProps) => {
  const { value, setBufferedValue, flush } = useBufferedInput(row.name, (nextValue) => {
    onChange?.(row, 'name', nextValue);
  });

  return (
    <TextField
      fullWidth
      size="small"
      value={value}
      error={!value.trim()}
      disabled={disabled}
      onClick={(event) => event.stopPropagation()}
      onKeyDown={(event) => event.stopPropagation()}
      onBlur={flush}
      onChange={(event) => setBufferedValue(event.target.value)}
      inputProps={{ 'aria-label': `Nome do benefício ${row.benefitId}` }}
      sx={{
        '& .MuiOutlinedInput-root': {
          height: 38,
          bgcolor: 'common.white',
          boxShadow: '0 2px 6px rgba(0, 0, 0, 0.12)',
        },
      }}
    />
  );
});

BenefitNameEditCell.displayName = 'BenefitNameEditCell';

const BenefitPriorityEditCell = memo(({ row, disabled, onChange }: BulkEditCellProps) => {
  const externalValue = row.priority === null ? '' : String(row.priority);
  const { value, setBufferedValue, flush } = useBufferedInput(externalValue, (nextValue) => {
    onChange?.(row, 'priority', nextValue === '' ? null : Number(nextValue));
  });

  const handleChange = (rawValue: string) => {
    if (rawValue === '') {
      setBufferedValue(rawValue);
      return;
    }

    if (!/^\d+$/.test(rawValue)) {
      return;
    }

    const priority = Number(rawValue);
    if (Number.isSafeInteger(priority)) {
      setBufferedValue(rawValue);
    }
  };

  return (
    <TextField
      fullWidth
      size="small"
      type="number"
      value={value}
      disabled={disabled}
      onClick={(event) => event.stopPropagation()}
      onKeyDown={(event) => event.stopPropagation()}
      onBlur={flush}
      onChange={(event) => handleChange(event.target.value)}
      inputProps={{
        'aria-label': `Prioridade do benefício ${row.benefitId}`,
        min: 0,
        step: 1,
      }}
      sx={{
        '& .MuiOutlinedInput-root': {
          height: 38,
          bgcolor: 'common.white',
          boxShadow: '0 2px 6px rgba(0, 0, 0, 0.12)',
        },
      }}
    />
  );
});

BenefitPriorityEditCell.displayName = 'BenefitPriorityEditCell';

function translateTier(tier?: string) {
  switch (tier?.toLowerCase()) {
    case 'all':
      return 'Todos';
    case 'silver':
      return 'prata';
    case 'gold':
      return 'ouro';
    case 'diamond':
      return 'diamante';
    default:
      return tier || 'Sem Tier';
  }
}

const tierStyles: Record<string, any> = {
  todos: {
    background: 'linear-gradient(180deg, #6f7680 0%, #4e5660 100%)',
    color: '#ffffff',
    boxShadow:
      '0 2px 6px rgba(33, 40, 48, 0.45), inset 0 1px 1px rgba(255, 255, 255, 0.18), inset 0 -1px 1px rgba(0, 0, 0, 0.18)',
    textShadow: '0 1px 1px rgba(0, 0, 0, 0.35)',
  },
  prata: {
    background:
      'linear-gradient(135deg, #f0f0f0 0%, #d9d9d9 20%, #bfbfbf 40%, #e6e6e6 60%, #c0c0c0 80%, #f5f5f5 100%)',
    color: '#1a1a1a',
    boxShadow:
      '0 2px 6px rgba(160, 160, 160, 0.6), inset 0 1px 2px rgba(255, 255, 255, 0.7), inset 0 -1px 2px rgba(120, 120, 120, 0.2)',
    textShadow: '0 1px 1px rgba(255, 255, 255, 0.8)',
  },
  ouro: {
    background:
      'linear-gradient(135deg, #fff6d0 0%, #ffec99 20%, #ffd84d 40%, #f5c842 60%, #eec44d 80%, #fff2b8 100%)',
    color: '#3a2d00',
    boxShadow:
      '0 2px 6px rgba(230, 180, 60, 0.5), inset 0 1px 2px rgba(255, 245, 200, 0.7), inset 0 -1px 2px rgba(190, 150, 40, 0.25)',
    textShadow: '0 1px 1px rgba(255, 250, 210, 0.85)',
  },
  diamante: {
    background:
      'linear-gradient(135deg, #e0f7ff 0%, #b8eaff 20%, #a0e0f0 40%, #c8f4ff 60%, #e6fbff 80%, #f4fdff 100%)',
    color: '#004040',
    boxShadow:
      '0 2px 8px rgba(140, 220, 240, 0.5), inset 0 1px 2px rgba(255, 255, 255, 0.8), inset 0 -1px 2px rgba(120, 180, 200, 0.3)',
    textShadow: '0 1px 1px rgba(255, 255, 255, 0.85)',
  },
};

export const getBenefitsColumns = ({
  formatDate,
  formatPoints,
  handleChangeStatus,
  tierColumnWidth,
  isBulkEditMode = false,
  isBulkEditLocked = false,
  onBulkEditFieldChange,
}: GetBenefitsColumnsParams): GridColDef[] => {
  const columns: GridColDef[] = [
    {
      field: 'image',
      headerName: 'Imagem',
      width: 112,
      align: 'center',
      headerAlign: 'center',
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params: GridRenderCellParams) => (
        <BenefitImageCell value={params.value} name={String(params.row?.name || 'Benefício')} />
      ),
    },
    {
      field: 'name',
      headerName: 'Nome',
      flex: 1.2,
      minWidth: 220,
      align: 'left',
      headerAlign: 'left',
      renderCell: (params: GridRenderCellParams) => {
        if (!isBulkEditMode) {
          return renderReadOnlyTextCell(String(params.value || '').trim());
        }

        return (
          <BenefitNameEditCell
            row={params.row}
            disabled={isBulkEditLocked}
            onChange={onBulkEditFieldChange}
          />
        );
      },
    },
    {
      field: 'priority',
      headerName: 'Prioridade',
      minWidth: 110,
      align: 'left',
      headerAlign: 'left',
      renderCell: (params: GridRenderCellParams) => {
        if (!isBulkEditMode) {
          return renderReadOnlyTextCell(
            typeof params.value === 'number' && Number.isFinite(params.value) ? params.value : '-'
          );
        }

        return (
          <BenefitPriorityEditCell
            row={params.row}
            disabled={isBulkEditLocked}
            onChange={onBulkEditFieldChange}
          />
        );
      },
    },
    {
      field: 'company',
      headerName: 'Empresa',
      flex: 1,
      minWidth: 200,
      align: 'left',
      headerAlign: 'left',
    },
    {
      field: 'tier',
      headerName: 'Tier',
      headerAlign: 'left',
      align: 'left',
      width: tierColumnWidth,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params: GridRenderCellParams) => {
        const rowTiers = Array.isArray(params.row.visibleForTiers)
          ? params.row.visibleForTiers
          : [];
        let normalizedTierValues = [String(params.row.tier || 'all')];

        if (rowTiers.length > 0) {
          normalizedTierValues = rowTiers.includes('all') ? ['all'] : rowTiers;
        }

        return (
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.8, width: 1 }}>
            {normalizedTierValues.map((tierValue: string) => {
              const translatedTier = translateTier(tierValue);
              const capitalizedTier =
                translatedTier.charAt(0).toUpperCase() + translatedTier.slice(1).toLowerCase();

              return (
                <Tooltip
                  key={`${params.id}-${tierValue}`}
                  title={capitalizedTier}
                  slotProps={{ tooltip: { sx: { borderRadius: 1 } } }}
                >
                  <Label
                    variant="soft"
                    sx={{
                      px: 2,
                      py: 0.5,
                      borderRadius: 2,
                      fontWeight: 'bold',
                      ...tierStyles[translatedTier.toLowerCase()],
                    }}
                  >
                    {translatedTier}
                  </Label>
                </Tooltip>
              );
            })}
          </Box>
        );
      },
    },
    {
      field: 'pointsValue',
      headerName: 'Valor (em pontos)',
      minWidth: 160,
      flex: 0.9,
      align: 'left',
      headerAlign: 'left',
      valueFormatter: ({ value }: { value: unknown }) => formatPoints(Number(value || 0)),
    },
    {
      field: 'type',
      headerName: 'Tipo',
      minWidth: 180,
      flex: 0.9,
      align: 'left',
      headerAlign: 'left',
      renderCell: (params: GridRenderCellParams) => <BenefitTypeCell value={params.value} />,
    },
    {
      field: 'exhibitionEnv',
      headerName: 'Origem',
      minWidth: 160,
      flex: 0.8,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params: GridRenderCellParams) => <ExhibitionOriginChip value={params.value} />,
    },
    {
      field: 'isNewBenefit',
      headerName: 'Novidade?',
      sortable: false,
      minWidth: 120,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params: GridRenderCellParams) => (
        <Label variant="soft" color={params.value === true ? 'info' : 'default'}>
          {params.value === true ? 'Sim' : 'Não'}
        </Label>
      ),
    },
    {
      field: 'totalQuantity',
      headerName: 'Quantidade total',
      minWidth: 170,
      align: 'left',
      headerAlign: 'left',
      renderCell: (params: GridRenderCellParams) => {
        if (params.row.isParking) {
          return '-';
        }

        return String(params.value ?? '-');
      },
    },
    {
      field: 'availableQuantity',
      headerName: 'Quantidade disponível',
      minWidth: 200,
      align: 'left',
      headerAlign: 'left',
      renderCell: (params: GridRenderCellParams) => {
        if (params.row.isParking) {
          return '-';
        }

        return String(params.value ?? '-');
      },
    },
    {
      field: 'redeemedQuantity',
      headerName: 'Quantidade resgatada',
      minWidth: 200,
      align: 'left',
      headerAlign: 'left',
    },
    {
      field: 'withdrawnQuantity',
      headerName: 'Quantidade retirada',
      minWidth: 180,
      align: 'left',
      headerAlign: 'left',
    },
    {
      field: 'startDate',
      headerName: 'Data de início',
      minWidth: 140,
      align: 'left',
      headerAlign: 'left',
      renderCell: (params: GridRenderCellParams) => (
        <BenefitDateCell value={params.value} formatDate={formatDate} />
      ),
    },
    {
      field: 'endDate',
      headerName: 'Data de fim',
      minWidth: 140,
      align: 'left',
      headerAlign: 'left',
      renderCell: (params: GridRenderCellParams) => (
        <BenefitDateCell value={params.value} formatDate={formatDate} />
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      minWidth: 120,
      flex: 0.7,
      align: 'left',
      headerAlign: 'left',
      renderHeader: () => <BenefitStatusHeader />,
      renderCell: (params: GridRenderCellParams) => (
        <BenefitStatusCell
          value={params.value}
          disabled={isBulkEditMode}
          onChange={(nextStatus) =>
            handleChangeStatus(String(params.row.benefitId || params.id || ''), nextStatus)
          }
        />
      ),
    },
  ];

  return columns.map((column) => {
    const isEditableColumn = column.field === 'name' || column.field === 'priority';
    const { cellClassName: defaultCellClassName } = column;
    let cellClassName = defaultCellClassName;

    if (isBulkEditMode) {
      cellClassName = isEditableColumn
        ? 'benefits-bulk-edit-cell'
        : 'benefits-bulk-edit-readonly-cell';
    }

    return {
      ...column,
      disableColumnMenu: true,
      cellClassName,
      renderCell: isBulkEditMode
        ? column.renderCell
        : (params: GridRenderCellParams) => {
            const content = column.renderCell
              ? column.renderCell(params)
              : (params.formattedValue ?? params.value ?? '');

            return typeof content === 'string' || typeof content === 'number'
              ? renderReadOnlyTextCell(content)
              : content;
          },
    };
  });
};
